import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import MiniDrawer from "../../../components/MiniDrawer/MiniDrawer";
import Wrapper from "../../../components/UI/Wrapper";
import Loader from "../../../components/Loader/loader";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";



// ─── Helpers ──────────────────────────────────────────────────────────────────
const baseAPI = process.env.REACT_APP_API_URL;

const getToken = () => {
  try { return JSON.parse(localStorage.getItem("token")); } catch { return null; }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDisplay = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const currency = (v) =>
  v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

// ─── Main Component ───────────────────────────────────────────────────────────
const ExpenseReport = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // DateRangePicker state (mirrors OrderList pattern)
  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState("Custom Dates");
  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(),
    key: "selection",
  });

  // Filter dropdowns
  const [selRelocation, setSelRelocation] = useState("0");
  const [selAddedBy, setSelAddedBy] = useState("0");
  const [selVendor, setSelVendor] = useState("0");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      };
      const res = await fetch(
        `${baseAPI}userGet/GetExpenseDetails?expense_detail_id=0&relocation_id=0`,
        { headers }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setAllData(json.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ── DateRangePicker handlers (same as OrderList) ───────────────────────────
  const togglePicker = () => {
    setDateRange(
      formatDate(selectionRange.startDate) + " - " + formatDate(selectionRange.endDate)
    );
    setIsOpen(!isOpen);
  };

  const handleSelect = (ranges) => {
    setSelectionRange(ranges.selection);
  };

  const clearDateRange = () => {
    setSelectionRange({
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(),
      key: "selection",
    });
  };

  // ── Derived dropdown options ───────────────────────────────────────────────
  const relocationOptions = [...new Map(allData.map((d) => [d.relocation_id, d.relocation_id])).values()]
    .sort((a, b) => a - b);

  const addedByOptions = [...new Set(allData.map((d) => d.added_by).filter(Boolean))].sort();
  const vendorOptions = [...new Set(allData.map((d) => d.vendor_name).filter(Boolean))].sort();

  // ── Filter logic ──────────────────────────────────────────────────────────
  const applyFilters = () => {
    setCurrentPage(1);
    if (isOpen) {
      setDateRange(
        formatDate(selectionRange.startDate) + " - " + formatDate(selectionRange.endDate)
      );
      setIsOpen(false);
    }
  };

  const clearFilters = () => {
    setSelRelocation("0");
    setSelAddedBy("0");
    setSelVendor("0");
    setDateRange("Custom Dates");
    clearDateRange();
    setCurrentPage(1);
  };

  const startStr = selectionRange.startDate.toISOString().split("T")[0];
  const endStr = selectionRange.endDate.toISOString().split("T")[0];

  const filtered = allData.filter((d) => {
    if (dateRange !== "Custom Dates") {
      const expDate = d.expense_date ? d.expense_date.split("T")[0] : "";
      if (expDate < startStr || expDate > endStr) return false;
    }
    if (selRelocation !== "0" && String(d.relocation_id) !== selRelocation) return false;
    if (selAddedBy !== "0" && d.added_by !== selAddedBy) return false;
    if (selVendor !== "0" && d.vendor_name !== selVendor) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // ── Summaries ─────────────────────────────────────────────────────────────
  // const totalExpense = filtered.reduce((s, d) => s + (Number(d.expense_amount) || 0), 0);
  // const totalSale = filtered.reduce((s, d) => s + (Number(d.sale_amount) || 0), 0);
  // const totalGP = totalSale - totalExpense;
  // const paidCount = filtered.filter((d) => d.payment_status_name === "Paid").length;

  // ── Excel Export ──────────────────────────────────────────────────────────
  const exportToExcel = () => {
    const rows = filtered.map((d, i) => ({
      "#": i + 1,
      "Relocation ID": d.relocation_id,
      "Date": fmtDisplay(d.expense_date),
      "Bill No.": d.bill_no || "",
      "Expense Description": d.description || "",
      "Expense Category": d.expense_category_name || "",
      "Vendor Name": d.vendor_name || "",
      "Location": d.location_name || "",
      "Qty": d.quantity ?? "",
      "Rate": d.rate ?? "",
      "Sale Amount (Rs)": d.sale_amount ?? 0,
      "Expense Amount (Rs)": d.expense_amount ?? 0,
      "GST Amount (Rs)": d.gst_amount ?? 0,
      "TDS Amount (Rs)": d.tds_amount ?? 0,
      "Payable to Vendor (Rs)": d.payable_to_vendor ?? 0,
      "Gross Profit (Rs)": (Number(d.sale_amount) || 0) - (Number(d.expense_amount) || 0),
      "Payment Mode": d.payment_mode_name || "",
      "Payment Made By": d.payment_made_by_name || "",
      "Payment Status": d.payment_status_name || "",
      "Added By": d.added_by || "",
      "Added Date": fmtDisplay(d.added_date),
      "Remarks": d.remarks || "",
    }));

    rows.push({});


    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = [4, 14, 12, 12, 32, 18, 22, 14, 5, 8, 16, 18, 14, 14, 20, 16, 14, 18, 14, 14, 14, 28];
    ws["!cols"] = colWidths.map((w) => ({ wch: w }));
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Expense Report");
    XLSX.writeFile(wb, `Expense_Report_${startStr}_to_${endStr}.xlsx`);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="app-container-orderlist">
      <MiniDrawer />
      <div className="container-fluid container-orderlist">

        {/* ── Page Header ── */}
        <div className="country-header">
          <div>
            <h1 style={{ fontSize: "24px", marginBottom: "1rem", lineHeight: "1.7" }}>
              Expense Report | <HomeOutlinedIcon />{" "}
              <span style={{ fontSize: "20px" }}>&gt; Expense Report</span>
            </h1>
          </div>
          <div className="lead-list-button">
            <p style={{
              borderColor: "#C0C6CC", border: "1px solid", textAlign: "center",
              padding: "10px", fontSize: "13px", width: "11rem", height: "2.5rem",
              borderRadius: "0.5rem",
            }}>
              <b>Total Records: </b>
              <span style={{ color: "#5B489D" }}><b>{filtered.length}</b></span>
            </p>
          </div>
        </div>

        {/* ── Filter Bar ── */}
        <Wrapper className="no-shadow">
          <div className="content-container-orderlist" style={{ paddingBottom: '1rem' }}>
            <div className="container-fluid">
              <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", alignItems: "flex-end" }}>

                {/* Relocation ID */}
                <div>
                  <select value={selRelocation} onChange={(e) => setSelRelocation(e.target.value)} style={selectStyle}>
                    <option value="0">Relocation ID</option>
                    {relocationOptions.map((id) => (
                      <option key={id} value={String(id)}>#{id}</option>
                    ))}
                  </select>
                </div>

                {/* Added By */}
                <div>
                  <select value={selAddedBy} onChange={(e) => setSelAddedBy(e.target.value)} style={selectStyle}>
                    <option value="0">Added By</option>
                    {addedByOptions.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>

                {/* Vendor */}
                <div>
                  <select value={selVendor} onChange={(e) => setSelVendor(e.target.value)} style={{ ...selectStyle, width: "12rem" }}>
                    <option value="0">Vendor Name</option>
                    {vendorOptions.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>

                {/* Date Range — exact OrderList pattern */}
                <div>
                  <button style={selectStyle} onClick={togglePicker}>
                    <b>{dateRange}</b>
                  </button>
                  {isOpen && (
                    <div style={{
                      position: "absolute", zIndex: 999,
                      border: "solid 1px black", width: "35pc",
                      background: "white",
                    }}>
                      <DateRangePicker ranges={[selectionRange]} onChange={handleSelect} />
                      <button
                        onClick={clearDateRange}
                        style={{
                          marginBottom: "1rem", width: "5rem", height: "2.5rem",
                          borderRadius: "0.5rem", fontSize: "13px",
                          position: "relative", top: "1px", left: "22pc",
                          color: "red", border: "none",
                        }}
                      >Reset</button>
                      <button
                        onClick={togglePicker}
                        style={{
                          marginBottom: "1rem", width: "7rem", height: "2.5rem",
                          borderRadius: "0.5rem", fontSize: "13px",
                          position: "relative", top: "1px", left: "22pc",
                          backgroundColor: "#5B489D", color: "white",
                        }}
                      >Done</button>
                    </div>
                  )}
                </div>

                {/* Apply */}
                <div className="orderlist-filter" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", marginTop: "6px", width: "8rem" }}>
                  <button onClick={applyFilters}>Apply</button>
                </div>

                {/* Clear Filter */}
                <div className="orderlist-filter-clear" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", marginTop: "6px", width: "8rem" }}>
                  <button style={{ marginRight: "34px", fontWeight: "600", color: "#F15843" }} onClick={clearFilters}>
                    Clear Filter
                  </button>
                </div>

                {/* Export to Excel */}
                <div className="Export-to_Excel" style={{ marginLeft: "auto", marginTop: "6px" }}>
                  <button
                    onClick={exportToExcel}
                    disabled={filtered.length === 0}
                    style={{
                      height: "2.5rem", padding: "0 16px", borderRadius: "0.5rem",
                      fontSize: "13px", fontWeight: "bold",
                      cursor: filtered.length === 0 ? "not-allowed" : "pointer",
                      backgroundColor: filtered.length === 0 ? "#ccc" : "#217346",
                      color: "white", border: "none",
                      display: "flex", alignItems: "center", gap: 6,
                    }}
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 3v13M7 11l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M4 20h16" strokeLinecap="round" />
                    </svg>
                    Export to Excel
                  </button>
                </div>

              </div>
            </div>
          </div>
        </Wrapper>

        {/* ── Summary Cards ── */}
        {/* <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", margin: "14px 0" }}>
          {[
            { label: "Total Expense", value: currency(totalExpense), color: "#DC3545" },
            { label: "Total Sales", value: currency(totalSale), color: "#5B489D" },
            { label: "Gross Profit", value: currency(totalGP), color: totalGP >= 0 ? "#28A745" : "#FFC107" },
            { label: "Paid Entries", value: `${paidCount} / ${filtered.length}`, color: "#17A2B8" },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              background: "white", borderRadius: "8px", padding: "10px 18px",
              borderLeft: `4px solid ${color}`, minWidth: 150,
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            }}>
              <div style={{ fontSize: "11px", color: "#6A6A6A", fontWeight: "600", marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: "16px", fontWeight: "800", color: "#1f2937" }}>{value}</div>
            </div>
          ))}
        </div> */}

        {/* ── Table ── */}
        <div className="sscroll">
          <Wrapper className="orderlist">
            <div className="content-container-orderlist">
              {loading ? (
                <Loader />
              ) : error ? (
                <div style={{ padding: 40, textAlign: "center", color: "#DC3545" }}>⚠️ {error}</div>
              ) : (
                <div className="container-fluid" style={{ width: "auto" }}>
                  <table className="orderlist" style={{ width: "100%" }}>
                    <thead>
                      <tr className="orderlistHeading">
                        <th>#</th>
                        <th>Rel. ID</th>
                        <th>Date</th>
                        <th>Bill No.</th>
                        <th>Description</th>
                        <th>Category</th>
                        <th>Vendor</th>
                        <th>Qty</th>
                        <th>Sale Amt</th>
                        <th>Exp. Amt</th>
                        <th>GST</th>
                        <th>TDS</th>
                        <th>Payable</th>
                        <th>Gross Profit</th>
                        <th>Pay Mode</th>
                        <th>Pay Status</th>
                        <th>Added By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.length > 0 ? (
                        paginated.map((d, i) => {
                          const gp = (Number(d.sale_amount) || 0) - (Number(d.expense_amount) || 0);
                          return (
                            <tr key={d.expense_detail_id}>
                              <td>{(currentPage - 1) * PAGE_SIZE + i + 1}</td>
                              <td style={{ color: "#5B489D", fontWeight: "bold" }}>{d.relocation_id}</td>
                              <td style={{ whiteSpace: "nowrap" }}>{fmtDisplay(d.expense_date)}</td>
                              <td>{d.bill_no || "—"}</td>
                              <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.description}>
                                {d.description || "—"}
                              </td>
                              <td>
                                <span style={{
                                  background: "#EEE9FF", color: "#5B489D",
                                  padding: "2px 8px", borderRadius: 10,
                                  fontSize: 11, fontWeight: 600,
                                }}>
                                  {d.expense_category_name || "—"}
                                </span>
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>{d.vendor_name || "—"}</td>
                              <td style={{ textAlign: "right" }}>{d.quantity ?? "—"}</td>
                              <td style={{ textAlign: "right" }}>{currency(d.sale_amount)}</td>
                              <td style={{ textAlign: "right", fontWeight: "bold" }}>{currency(d.expense_amount)}</td>
                              <td style={{ textAlign: "right" }}>{currency(d.gst_amount)}</td>
                              <td style={{ textAlign: "right" }}>{currency(d.tds_amount)}</td>
                              <td style={{ textAlign: "right" }}>{currency(d.payable_to_vendor)}</td>
                              <td style={{ textAlign: "right", fontWeight: "bold", color: gp >= 0 ? "#28A745" : "#DC3545" }}>
                                {currency(gp)}
                              </td>
                              <td>{d.payment_mode_name || "—"}</td>
                              <td>
                                <b style={{
                                  padding: "4px 10px", borderRadius: "15px", fontSize: "12px",
                                  color: "white", fontWeight: "bold", whiteSpace: "nowrap",
                                  backgroundColor: d.payment_status_name === "Paid" ? "#28A745" : "orange",
                                }}>
                                  {d.payment_status_name || "Pending"}
                                </b>
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>{d.added_by || "—"}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={17} style={{ textAlign: "center", padding: "2rem", color: "#6A6A6A" }}>
                            No expenses found for the selected filters.
                          </td>
                        </tr>
                      )}
                    </tbody>

                    {/* Totals footer */}
                    {/* {filtered.length > 0 && (
                      <tfoot>
                        <tr style={{ backgroundColor: "#EEE9FF", fontWeight: "bold" }}>
                          <td colSpan={8} style={{ padding: "10px 14px", color: "#5B489D", fontSize: "12px" }}>
                            TOTALS ({filtered.length} records)
                          </td>
                          <td style={{ textAlign: "right", padding: "10px 14px", color: "#5B489D" }}>{currency(totalSale)}</td>
                          <td style={{ textAlign: "right", padding: "10px 14px", color: "#DC3545" }}>{currency(totalExpense)}</td>
                          <td colSpan={3} style={{ padding: "10px 14px" }} />
                          <td style={{ textAlign: "right", padding: "10px 14px", color: totalGP >= 0 ? "#28A745" : "#DC3545" }}>{currency(totalGP)}</td>
                          <td colSpan={3} style={{ padding: "10px 14px" }} />
                        </tr>
                      </tfoot>
                    )} */}
                  </table>

                  {/* Pagination — same pattern as OrderList */}
                  <nav aria-label="Page navigation" style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
                    <ul className="pagination">
                      <li className="page-item">
                        <a style={{ cursor: "pointer" }} className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                          Previous
                        </a>
                      </li>
                      {currentPage > 1 && (
                        <li className="page-item">
                          <a style={{ cursor: "pointer" }} className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                            {currentPage - 1}
                          </a>
                        </li>
                      )}
                      <li className="page-item active">
                        <a className="page-link">{currentPage}</a>
                      </li>
                      {currentPage < totalPages && (
                        <li className="page-item">
                          <a style={{ cursor: "pointer" }} className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                            {currentPage + 1}
                          </a>
                        </li>
                      )}
                      <li className="page-item">
                        <a style={{ cursor: "pointer" }} className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                          Next
                        </a>
                      </li>
                    </ul>
                  </nav>

                </div>
              )}
            </div>
          </Wrapper>
        </div>

        <style>{`
          .orderlist-filter button {
            margin-top: -54px;
        }
            .orderlist-filter-clear button {
              margin-top: -54px;
            }
              .Export-to_Excel button {
                margin-top: -54px;
              }
                .no-shadow {
  box-shadow: none !important;
}

.table-container {
  max-height: 400px;   /* or whatever height you want */
  overflow-y: auto;
}
.orderlistHeading th {
  position: sticky;
  top: 0;
 
  z-index: 2;         /* keeps it above table body */
}
  .orderlistHeading th {
    background-color: #f1f1f1;
    color: #333;
  }
    tbody tr td {font-size: 14px; color: #333;}

   .orderlist td, .orderlist th {
    border: 1px solid #ddd;
    padding: 13px 12px;
  }
            `}
        </style>
      </div>
    </div>
  );
};

export default ExpenseReport;

// Shared select style matching OrderList inputs
const selectStyle = {
  marginBottom: "1rem",
  height: "2.5rem",
  borderRadius: "0.5rem",
  fontSize: "13px",
  fontWeight: "bold",
  marginTop: "6px",
  width: "10rem",
};

